/**
 * **인플루언서 질문의 mockup 갈래** — 멘션 쪽 `actions/els/els-mock` 과 **같은 모양**이다.
 *
 * ```
 *   actions/els/influencer.ts      list.page · list.refs · list.facets · docs.byIds · doc.get 머리에서 resolveMock*() 한 줄
 *     │  잡히면 주입본, 아니면
 *     ▼
 *   es-request → /api/els/influencers    평소 그대로
 * ```
 *
 * ## ⚠️ 판정은 **「등록됐는가」** 하나다
 * 이 질문들의 params 는 필터·정렬·페이지·계정 이름·문서 id 뿐이고(인플루언서가 **결과**인 질문이다),
 * 문서 id 가 `{채널}_{계정}` 꼴이라 `MOCKUP_` 접두를 실을 자리가 없다. 등록은 인플루언서·허브
 * mockup 라우트의 layout 이 마운트돼 있는 동안에만 살아 있고 언마운트에 풀리므로, 실화면 호출은
 * 여기 닿지 않는다.
 *
 * ## ⚠️ 왜 멘션 레지스트리와 따로인가
 * 이 화면들은 인플루언서 인덱스(목록)와 멘션 인덱스(계정 검색·미리보기) **둘 다** 묻는다.
 * 경계 파일이 둘이라 레지스트리도 그 축을 따라 둘이고, 켜고 끄는 것은 각 mockup backend 가 한 번에 한다.
 *
 * ## ⚠️ 타입을 좁혀 둔 이유
 * 핸들러마다 Request/Response 를 경계 타입으로 못박았다. op 계약이 바뀌면 **주입 변환기가
 * tsc 에서 운다** — 단언(`as`)으로 헐겁게 만들면 그 경보를 잃는다.
 *
 * ⚠️ 핸들러가 `null` 을 주면 「내 것이 아니다」라는 뜻이고 호출은 평소대로 서버로 간다.
 * 등록이 없으면(기본) 모든 질문이 그대로 나간다 — 실화면 동작 무변.
 */
import type {
  InfluencerDocGetRequest,
  InfluencerFacetsRequest,
  InfluencerDocGetResponse,
  InfluencerFacetsResponse,
  InfluencerListPageRequest,
  InfluencerListRefsRequest,
  InfluencerDocsByIdsRequest,
  InfluencerListPageResponse,
  InfluencerListRefsResponse,
  InfluencerDocsByIdsResponse,
} from 'src/types/influencer/influencer-ops';

export type InfluencerMockHandlers = {
  /** 목록 한 페이지 (`list.page`) — 키워드가 없을 때의 갈래 · 크리에이터 찾기 · 비슷한 크리에이터의 셋째 켜 */
  listPage?: (params: InfluencerListPageRequest) => InfluencerListPageResponse | null;
  /** 참조 목록 (`list.refs`) — 키워드가 걸렸을 때의 1단계 · 계정 이름으로 문서 id 찾기 */
  listRefs?: (params: InfluencerListRefsRequest) => InfluencerListRefsResponse | null;
  /** 성별·연령대 고르개 (`list.facets`) */
  facets?: (params: InfluencerFacetsRequest) => InfluencerFacetsResponse | null;
  /** ID 목록 조회 (`docs.byIds`) — 참조로 고른 쪽의 문서 · 얼굴·팔로워. **입력 순서를 지킨다** */
  docsByIds?: (params: InfluencerDocsByIdsRequest) => InfluencerDocsByIdsResponse | null;
  /** 문서 한 건 (`doc.get`) — 오디언스 리포트 */
  docGet?: (params: InfluencerDocGetRequest) => InfluencerDocGetResponse | null;
};

/** mockup 라우트만 부른다. `null` 이면 해제다 */
export function registerInfluencerMock(next: InfluencerMockHandlers | null): void {
  handlers = next;
}

export function resolveMockInfluencerListPage(
  params: InfluencerListPageRequest,
): InfluencerListPageResponse | null {
  return handlers?.listPage?.(params) ?? null;
}

export function resolveMockInfluencerListRefs(
  params: InfluencerListRefsRequest,
): InfluencerListRefsResponse | null {
  return handlers?.listRefs?.(params) ?? null;
}

export function resolveMockInfluencerFacets(
  params: InfluencerFacetsRequest,
): InfluencerFacetsResponse | null {
  return handlers?.facets?.(params) ?? null;
}

export function resolveMockInfluencerDocsByIds(
  params: InfluencerDocsByIdsRequest,
): InfluencerDocsByIdsResponse | null {
  return handlers?.docsByIds?.(params) ?? null;
}

export function resolveMockInfluencerDoc(
  params: InfluencerDocGetRequest,
): InfluencerDocGetResponse | null {
  return handlers?.docGet?.(params) ?? null;
}

let handlers: InfluencerMockHandlers | null = null;
